import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { useTimer } from '@/contexts/timer-context';
import { formatTime as formatTimeUtil } from '@/utils/timer-utils';
import { Eye, EyeOff, Pause, Play, Square } from 'lucide-react';

interface ActiveSessionCardProps {
  onSessionEnd: () => void;
}

export default function ActiveSessionCard({ onSessionEnd }: ActiveSessionCardProps) {
  // Get timer state from context
  const {
    isTimerRunning,
    sessionSeconds,
    pauseSession,
    resumeSession,
    endSession,
    isTimerVisible,
    showTimer,
    hideTimer,
  } = useTimer();

  // If session is not running and no time elapsed, don't show card
  const isActive = isTimerRunning || sessionSeconds > 0;

  if (!isActive) {
    return null;
  }

  const handleEndSession = async () => {
    // Attempt to get project ID from URL if possible, otherwise rely on context state
    const pathParts = window.location.pathname.split('/');
    const projectsIndex = pathParts.indexOf('projects');
    const projectId = projectsIndex >= 0 ? pathParts[projectsIndex + 1] : null;

    if (projectId) {
      await endSession(projectId);
    }
    onSessionEnd();
  };

  return (
    <Card className="mb-6 border-border bg-card/60 backdrop-blur-xl shadow-sm relative overflow-hidden animate-in fade-in zoom-in-95 duration-500 rounded-2xl">

      {/* Background Decor */}
      <div className="absolute top-0 right-0 p-24 bg-primary/5 rounded-full blur-3xl -mr-16 -mt-16 pointer-events-none" />
      <div className="absolute bottom-0 left-0 p-24 bg-blue-500/5 rounded-full blur-3xl -ml-16 -mb-16 pointer-events-none" />

      <div className="relative z-10 flex flex-col items-center justify-center py-6 px-6">

        {/* Status Indicator */}
        <div className="flex items-center gap-2 mb-4">
          <div className="relative flex h-3 w-3">
            <span className={`animate-ping absolute inline-flex h-full w-full rounded-full bg-green-500 opacity-75 ${!isTimerRunning ? 'hidden' : ''}`}></span>
            <span className={`relative inline-flex rounded-full h-3 w-3 ${isTimerRunning ? 'bg-green-500' : 'bg-orange-500'}`}></span>
          </div>
          <span className={`text-xs font-medium tracking-wide uppercase ${isTimerRunning ? 'text-green-500' : 'text-orange-500'}`}>
            {isTimerRunning ? 'Focus Session Active' : 'Session Paused'}
          </span>
        </div>

        {/* Timer Display */}
        <div className="mb-6 text-center">
          <div className={`text-6xl font-mono font-bold tracking-tighter tabular-nums leading-none transition-colors duration-300 ${isTimerRunning ? 'text-foreground' : 'text-muted-foreground'}`}>
            {formatTimeUtil(sessionSeconds)}
          </div>
          <p className="text-muted-foreground mt-1 text-xs font-medium">Total Time Elapsed</p>
        </div>

        {/* Controls */}
        <div className="flex items-center gap-3">
          <div className="flex items-center p-1.5 bg-muted/50 backdrop-blur-md rounded-xl border border-border/50 shadow-sm transition-all hover:bg-muted/70">
            {isTimerRunning ? (
              <Button
                onClick={pauseSession}
                variant="ghost"
                size="sm"
                className="h-10 w-10 rounded-lg hover:bg-background hover:text-orange-500 transition-all"
                title="Pause Session"
              >
                <Pause className="h-5 w-5 fill-current" />
              </Button>
            ) : (
              <Button
                onClick={resumeSession}
                variant="ghost"
                size="sm"
                className="h-10 w-10 rounded-lg hover:bg-background hover:text-green-500 transition-all"
                title="Resume Session"
              >
                <Play className="h-5 w-5 fill-current" />
              </Button>
            )}

            <div className="w-px h-6 bg-border/50 mx-2" />

            <Button
              onClick={handleEndSession}
              variant="ghost"
              size="sm"
              className="h-10 w-auto px-4 min-w-[100px] rounded-lg hover:bg-destructive/10 hover:text-destructive transition-all font-medium text-sm"
            >
              <Square className="mr-2 h-3.5 w-3.5 fill-current" />
              End Session
            </Button>
          </div>

          <Button
            onClick={isTimerVisible ? hideTimer : showTimer}
            variant="ghost"
            size="icon"
            className="h-12 w-12 rounded-full border border-border/50 hover:bg-muted/50 text-muted-foreground hover:text-foreground transition-all ml-1"
            title={isTimerVisible ? "Hide Timer Widget" : "Show Timer Widget"}
          >
            {isTimerVisible ? (
              <EyeOff className="h-4 w-4" />
            ) : (
              <Eye className="h-4 w-4" />
            )}
          </Button>
        </div>

      </div>
    </Card>
  );
}
